import {tv, type VariantProps} from 'tailwind-variants'
import {type ExtractStrict, type OmitStrict} from 'type-zoo'
import {TextTag} from '~/design-system/components'
import {
  HgButtonLink,
  type DefaultSizeSpecificButtonProps,
  type HgButtonLinkProps,
} from '~/design-system/hg/components/HgButton'
import HgEmailCapture, {
  type HgEmailCaptureProps,
} from '~/design-system/hg/components/HgEmailCapture'

export type HgHeadingButton = Omit<
  HgButtonLinkProps,
  'variant' | 'size' | 'iconType'
> &
  DefaultSizeSpecificButtonProps

export type HgHeadingEmailProps = {
  contentType: 'emailCapture'
  emailCaptureProps: HgEmailCaptureProps
}

export type HgHeadingButtonGroupProps = {
  contentType: 'buttonGroup'
  primaryButtonProps?: HgHeadingButton
  secondaryButtonProps?: HgHeadingButton
}

export type HgHeadingNoContent = {
  contentType: 'none'
}

export type HgHeadingContentTypeProps =
  | HgHeadingEmailProps
  | HgHeadingButtonGroupProps
  | HgHeadingNoContent

type HgHeadingTVProps = VariantProps<typeof hgHeadingVariants>

type HgHeadingBaseProps = {
  headline: React.ReactNode
  subhead?: React.ReactNode
  className?: string
} & OmitStrict<HgHeadingTVProps, 'topSpacing' | 'variation'>

export type HgHeadingHeroProps = {
  variation: 'hero'
  topSpacing?: ExtractStrict<HgHeadingTVProps['topSpacing'], 's0' | 's8'>
}

export type HgHeadingSectionProps = {
  variation: 'section'
  topSpacing?: ExtractStrict<HgHeadingTVProps['topSpacing'], 's0' | 's12'>
}

export type HgHeadingVariationProps = HgHeadingHeroProps | HgHeadingSectionProps

export type HgHeadingProps = HgHeadingVariationProps &
  HgHeadingContentTypeProps &
  HgHeadingBaseProps

const hgHeadingVariants = tv({
  slots: {
    title: 'grid text-text-default',
    container: 'flex flex-col',
    emailCaptureContainer: 'mt-s6 flex w-full sm:w-auto',
    buttonGroup: 'flex gap-s2',
    formItemWrapper: '',
  },
  variants: {
    topSpacing: {
      s0: {
        container: 'pt-s0',
      },
      s8: {
        container: 'pt-s8',
      },
      s12: {
        container: 'pt-s12',
      },
    },
    alignment: {
      left: {
        container: 'items-start text-left',
        emailCaptureContainer: 'items-start text-left',
      },
      center: {
        container: 'items-center text-center',
        emailCaptureContainer: 'items-center text-center',
        formItemWrapper: 'items-center',
      },
    },
    variation: {
      hero: {
        title: 'neue-montreal-display-2',
        buttonGroup: 'mt-s6',
      },
      section: {
        title: 'neue-montreal-heading-1',
        buttonGroup: 'mt-s5',
      },
    },
  },
})

function HgHeadingButtonGroup(
  props: Partial<Omit<HgHeadingButtonGroupProps, 'type'>> & {className: string}
) {
  if (!props.primaryButtonProps && !props.secondaryButtonProps) return null
  return (
    <div className={props.className}>
      {props.primaryButtonProps && (
        <HgButtonLink
          variant="primary"
          size="default"
          {...props.primaryButtonProps}
        />
      )}
      {props.secondaryButtonProps && (
        <HgButtonLink variant="secondary" {...props.secondaryButtonProps} />
      )}
    </div>
  )
}

export default function HgHeading({
  headline,
  subhead,
  alignment = 'center',
  className,
  ...props
}: HgHeadingProps) {
  const {container, title, emailCaptureContainer, buttonGroup, formItemWrapper} =
    hgHeadingVariants({
      topSpacing: props.topSpacing ?? 's0',
      alignment,
      variation: props.variation,
    })

  function renderVariation() {
    switch (props.contentType) {
      case 'emailCapture': {
        const {emailCaptureProps} = props

        return (
          <div className={emailCaptureContainer()}>
            <HgEmailCapture
              classNames={{formItemWrapper: formItemWrapper()}}
              secondaryLinkProps={
                emailCaptureProps.secondaryLinkProps
                  ? {
                      ...emailCaptureProps.secondaryLinkProps,
                      variant: 'withArrow',
                    }
                  : undefined
              }
              {...emailCaptureProps}
            />
          </div>
        )
      }
      case 'buttonGroup':
        return <HgHeadingButtonGroup {...props} className={buttonGroup()} />
      case 'none':
      default:
        return null
    }
  }
  return (
    <div className={container({class: className})}>
      <div className="flex flex-col gap-s3">
        <TextTag
          tag={props.variation === 'section' ? 'h2' : 'h1'}
          className={title()}
        >
          {headline}
        </TextTag>
        {subhead && (
          <p className="text-text-subdued neue-montreal-heading-2">{subhead}</p>
        )}
      </div>
      {renderVariation()}
    </div>
  )
}
